if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      // Lightbox elements
      this.lightbox = this.parentElement.querySelector('[data-media-lightbox]');
      this.lightboxContent = this.lightbox ? this.lightbox.querySelector('.media-lightbox__content') : null;
      this.lightboxCloseBtn = this.lightbox ? this.lightbox.querySelector('[data-media-lightbox-close]') : null;

      if (this.lightbox) {
        this.initLightbox();
      }

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        this.switchToVariantMedia(this.previousMediaItem);
        setTimeout(() => {
          this.customSetActiveMedia(this.previousMediaItem, false);
        }, 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
        this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
        window.addEventListener('on:debounced-resize', this.zoomInitHandler);
        this.initZoom();
        this.zoomLinks.forEach((el) => {
          el.addEventListener('click', (evt) => {
            // Only open lightbox on large screens (>= 1024px)
            if (!window.matchMedia('(min-width: 1024px)').matches) return;
            evt.preventDefault();
            this.openLightbox(el);
          });
        });
      }

      this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.switchToVariantMedia(variantMedia);
      }
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Called by variant-picker when a variant with featured media is selected.
     * On stacked layout: swaps the first image to the variant image without scrolling.
     * On carousel layout: slides to the variant image.
     * @param {Element} mediaItem - Media element to set as active.
     */
    setActiveMedia(mediaItem) {
      if (!mediaItem) return;
      this.switchToVariantMedia(mediaItem);
    }

    /**
     * Switches the displayed media to the selected variant's image.
     * On stacked layout, replaces the first item's image src with the variant image.
     * On carousel, scrolls to the variant item.
     * @param {Element} mediaItem - The variant media element.
     */
    switchToVariantMedia(mediaItem) {
      if (!mediaItem) return;

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        const firstItem = this.viewer.querySelector('.media-viewer__item');
        const firstImg = firstItem ? firstItem.querySelector('img') : null;

        if (!firstImg) return;

        // Store the original state the first time we touch this image
        if (!firstImg.dataset.originalSrc) {
          firstImg.dataset.originalSrc = firstImg.src;
          firstImg.dataset.originalSrcset = firstImg.srcset || '';
          firstImg.dataset.originalAlt = firstImg.alt || '';
        }

        const firstZoomLink = firstItem.querySelector('.js-zoom-link');
        const firstZoomImg = firstItem.querySelector('.js-zoom-image');
        if (firstZoomImg && !firstZoomImg.dataset.originalZoomSrc) {
          firstZoomImg.dataset.originalZoomSrc = firstZoomImg.src;
          firstZoomImg.dataset.originalZoomDataSrc = firstZoomImg.dataset.src || '';
          if (firstZoomLink) firstZoomLink.dataset.originalHref = firstZoomLink.href;
        }

        if (mediaItem === firstItem) {
          // Variant 1 — restore original first image and zoom
          firstImg.src = firstImg.dataset.originalSrc;
          firstImg.srcset = firstImg.dataset.originalSrcset;
          firstImg.alt = firstImg.dataset.originalAlt;

          if (firstZoomImg) {
            firstZoomImg.src = firstZoomImg.dataset.originalZoomSrc;
            if (firstZoomImg.dataset.originalZoomDataSrc) {
              firstZoomImg.dataset.src = firstZoomImg.dataset.originalZoomDataSrc;
              if (firstZoomLink) firstZoomLink.classList.add('media--zoom-not-loaded');
            } else {
              delete firstZoomImg.dataset.src;
              if (firstZoomLink) firstZoomLink.classList.remove('media--zoom-not-loaded');
            }
            if (firstZoomLink && firstZoomLink.dataset.originalHref) {
              firstZoomLink.href = firstZoomLink.dataset.originalHref;
            }
          }
        } else {
          const variantImg = mediaItem.querySelector('.product-image');
          if (variantImg) {
            firstImg.src = variantImg.dataset.src || variantImg.src;
            firstImg.srcset = variantImg.dataset.srcset || variantImg.srcset || '';
            firstImg.alt = variantImg.alt || '';
          }

          // Mirror the variant's zoom image onto the first item
          const variantZoomImg = mediaItem.querySelector('.js-zoom-image');
          const variantZoomLink = mediaItem.querySelector('.js-zoom-link');
          if (firstZoomImg && variantZoomImg) {
            if (variantZoomImg.dataset.src) {
              const preloadSrc = variantZoomImg.dataset.src;
              firstZoomImg.dataset.src = preloadSrc;
              firstZoomImg.src = firstZoomImg.dataset.originalZoomSrc || firstZoomImg.src;
              if (firstZoomLink) firstZoomLink.classList.add('media--zoom-not-loaded');

              // Preload in background so hover is instant
              this.loadingSpinner.classList.remove('loading-spinner--out');
              const preloadImg = new Image();
              preloadImg.src = preloadSrc;
              preloadImg.onload = () => {
                if (firstZoomImg.dataset.src === preloadSrc) {
                  firstZoomImg.src = preloadImg.src;
                  delete firstZoomImg.dataset.src;
                  delete variantZoomImg.dataset.src;
                  variantZoomImg.src = preloadImg.src;
                  if (firstZoomLink) firstZoomLink.classList.remove('media--zoom-not-loaded');
                  this.loadingSpinner.classList.add('loading-spinner--out');
                }
              };
            } else {
              // Variant zoom already loaded — use it directly
              firstZoomImg.src = variantZoomImg.src;
              delete firstZoomImg.dataset.src;
              if (firstZoomLink) firstZoomLink.classList.remove('media--zoom-not-loaded');
            }
            if (variantZoomLink && firstZoomLink) {
              firstZoomLink.href = variantZoomLink.href;
            }
          }
        }
        return;
      }

      // Carousel: use normal scroll-to behaviour
      this.customSetActiveMedia(mediaItem, true);
    }

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (scrollToItem && this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }

    initLightbox() {
      if (this.lightboxCloseBtn) {
        this.lightboxCloseBtn.addEventListener('click', () => this.closeLightbox());
      }

      this.lightbox.addEventListener('click', (evt) => {
        if (evt.target === this.lightbox) {
          this.closeLightbox();
        }
      });

      this.lightboxKeyHandler = (evt) => {
        if (!this.lightbox.hasAttribute('data-open')) return;
        if (evt.key === 'Escape') {
          this.closeLightbox();
        }
      };
      document.addEventListener('keydown', this.lightboxKeyHandler);

      this.viewer.querySelectorAll('.media-viewer__item[data-media-type="image"]').forEach((item) => {
        item.style.cursor = 'pointer';
        item.addEventListener('click', (evt) => {
          if (!window.matchMedia('(min-width: 1024px)').matches) return;
          if (evt.target.closest('a')) return;
          const img = item.querySelector('.product-image');
          if (img) {
            this.openLightbox(item);
          }
        });
      });
    }

    openLightbox(sourceElement) {
      if (!window.matchMedia('(min-width: 1024px)').matches) return;
      if (!this.lightbox || !this.lightboxContent) return;

      this.lightboxContent.innerHTML = '';
      const imageItems = this.visibleItems.filter((item) => item.dataset.mediaType === 'image');
      const clickedItem = sourceElement && sourceElement.closest
        ? sourceElement.closest('.media-viewer__item')
        : null;
      const lightboxImages = [];

      imageItems.forEach((item) => {
        const zoomLink = item.querySelector('.js-zoom-link');
        const img = item.querySelector('.product-image');
        const src = zoomLink ? zoomLink.href : (img ? img.src : '');
        if (!src) return;

        const lightboxImg = document.createElement('img');
        lightboxImg.className = 'media-lightbox__image';
        lightboxImg.src = src;
        lightboxImg.alt = img?.alt || '';
        lightboxImg.setAttribute('width', img?.naturalWidth || 1200);
        lightboxImg.setAttribute('height', img?.naturalHeight || 1200);

        lightboxImg.onload = () => {
          lightboxImg.setAttribute('data-loaded', '');
        };

        this.lightboxContent.appendChild(lightboxImg);
        lightboxImages.push(lightboxImg);
      });

      this.lightbox.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.lightbox.setAttribute('data-open', '');

        if (clickedItem && lightboxImages.length) {
          const clickedIndex = imageItems.indexOf(clickedItem);
          if (clickedIndex >= 0 && lightboxImages[clickedIndex]) {
            const target = lightboxImages[clickedIndex];
            this.lightboxContent.scrollTo({
              top: target.offsetTop,
              behavior: 'auto'
            });
          }
        }
      });

      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      if (!this.lightbox) return;

      this.lightbox.removeAttribute('data-open');
      setTimeout(() => {
        this.lightbox.setAttribute('hidden', '');
        if (this.lightboxContent) {
          this.lightboxContent.innerHTML = '';
        }
      }, 300);

      document.body.style.overflow = '';
    }

  }

  customElements.define('media-gallery', MediaGallery);
}